import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import ListIcon from '@mui/icons-material/List';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import TermsListRow from 'components/TermsListRow';
import TermsListAdd from 'components/TermsListAdd';

import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';
import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';

import Query, {
  TermsListRefetchQuery,
} from '__generated__/TermsListRefetchQuery.graphql';
import { TermsList_data$key } from '__generated__/TermsList_data.graphql';

type Props = {
  data: TermsList_data$key;
};

const TermsList: FC<Props> = ({ data }) => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [{ terms }, { refetch, loading }] = useAvoidSuspense<
    TermsListRefetchQuery,
    TermsList_data$key
  >(
    graphql`
      fragment TermsList_data on Query
      @refetchable(queryName: "TermsListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
      ) {
        terms(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id

              ...TermsListRow_term
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionId = terms?.__id;

  const [deleteRoomProperty, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = terms ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<ListIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableCell sx={{ width: '20px' }} />
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>

            <TableHeadCell center name="agreementBlock">
              Įrašyti prie sutarties
            </TableHeadCell>
            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <TermsListRow
            key={node.id}
            term={node}
            onDelete={deleteRoomProperty}
          />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm">
          <TermsListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default TermsList;
